<script setup>
import { computed, reactive, watch, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import {
  formatCapitalSocial,
  companyTipo,
} from 'dashboard/helper/companyHelper';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import CompanyAPI from 'dashboard/api/companies';
import CreateNewCompanyDialog from 'dashboard/components-next/Companies/CompaniesForm/CreateNewCompanyDialog.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const store = useStore();
const companyDropdownRef = ref(null);
const createCompanyDialogRef = ref(null);

const { t } = useI18n();

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  companyId: null,
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive({ ...defaultState });

// Estado para busca de empresas
const companySearchQuery = ref('');
const companySearchResults = ref([]);
const isSearchingCompany = ref(false);
const showCompanyDropdown = ref(false);
const selectedCompany = ref(null);

// Buscar empresas
const searchCompanies = async () => {
  if (!companySearchQuery.value || companySearchQuery.value.length < 2) {
    companySearchResults.value = [];
    showCompanyDropdown.value = false;
    return;
  }

  isSearchingCompany.value = true;
  try {
    const response = await CompanyAPI.search(
      companySearchQuery.value,
      1,
      'name'
    );
    companySearchResults.value = response.data.payload || response.data || [];
    showCompanyDropdown.value = companySearchResults.value.length > 0;
  } catch (error) {
    companySearchResults.value = [];
    showCompanyDropdown.value = false;
  } finally {
    isSearchingCompany.value = false;
  }
};

// Selecionar empresa
const selectCompany = company => {
  state.companyId = company.id;
  selectedCompany.value = company;
  companySearchQuery.value = company.name;
  showCompanyDropdown.value = false;
  companySearchResults.value = [];
  emit('update', state);
};

// Limpar empresa selecionada
const clearCompany = () => {
  state.companyId = null;
  selectedCompany.value = null;
  companySearchQuery.value = '';
  emit('update', state);
};

// Abrir dialog de criar empresa
const openCreateCompanyDialog = () => {
  showCompanyDropdown.value = false;
  createCompanyDialogRef.value?.dialogRef?.open();
};

// Handler quando empresa é criada
const handleCompanyCreated = async companyData => {
  await store.dispatch('companies/create', companyData);
  const companiesList = store.getters['companies/getCompaniesList'] || [];
  const newCompany = companiesList.find(c => c.name === companyData.name);
  if (newCompany) {
    selectCompany(newCompany);
  }
  createCompanyDialogRef.value?.onSuccess();
};

// Verificar se não encontrou empresa na busca
const showCreateCompanyOption = computed(() => {
  return (
    companySearchQuery.value.length >= 2 &&
    !isSearchingCompany.value &&
    companySearchResults.value.length === 0 &&
    !selectedCompany.value
  );
});

// Click outside handler para fechar dropdown
const handleClickOutside = event => {
  if (
    companyDropdownRef.value &&
    !companyDropdownRef.value.contains(event.target)
  ) {
    showCompanyDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

// Simple validation: firstName is required, email must be valid if provided
const isFormInvalid = computed(() => {
  // firstName is required
  if (!state.firstName || state.firstName.trim() === '') {
    return true;
  }
  // If email is provided, it must be valid
  if (state.email && state.email.trim() !== '' && v$.value.email.$invalid) {
    return true;
  }
  return false;
});

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    company_id: companyId,
    company,
    additionalAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  Object.assign(state, {
    id,
    name,
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    companyId: companyId || null,
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      socialProfiles,
    },
  });

  // Se tem empresa associada, preencher os campos de busca
  if (company) {
    selectedCompany.value = company;
    companySearchQuery.value = company.name;
  } else {
    selectedCompany.value = null;
    companySearchQuery.value = '';
  }
};

const companyAddress = computed(() => {
  const co = selectedCompany.value || {};
  return [co.address_line1, co.address_line2].filter(Boolean).join(' - ');
});

const companyCityState = computed(() => {
  const co = selectedCompany.value || {};
  return [co.city, co.state].filter(Boolean).join(' / ');
});

const companyOpeningDate = computed(() => {
  const date =
    selectedCompany.value?.additional_attributes?.data_inicio_atividade;
  if (!date) return null;
  const parts = date.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return date;
});

const companyAge = computed(() => {
  const date =
    selectedCompany.value?.additional_attributes?.data_inicio_atividade;
  if (!date) return null;
  const start = new Date(date);
  if (Number.isNaN(start.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years > 0)
    return `${years} ano${years > 1 ? 's' : ''}${months > 0 ? ` e ${months} ${months > 1 ? 'meses' : 'mês'}` : ''}`;
  return months > 0
    ? `${months} ${months > 1 ? 'meses' : 'mês'}`
    : 'Menos de 1 mês';
});

const companyCapitalSocial = computed(() =>
  formatCapitalSocial(
    selectedCompany.value?.additional_attributes?.capital_social
  )
);

const companyNaturezaJuridica = computed(
  () => selectedCompany.value?.additional_attributes?.natureza_juridica
);

const companyTipoLabel = computed(() =>
  companyTipo(selectedCompany.value?.additional_attributes)
);

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', state);
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, defaultState);
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

// Watch firstName and lastName to update name and emit changes
watch(
  () => [state.firstName, state.lastName],
  () => {
    state.name = `${state.firstName} ${state.lastName}`.trim();
    emit('update', state);
  }
);

// Watch all other fields to emit changes
watch(
  () => [
    state.email,
    state.phoneNumber,
    state.additionalAttributes.city,
    state.additionalAttributes.description,
  ],
  () => {
    emit('update', state);
  }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE') }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <!-- First Name -->
        <Input
          v-model="state.firstName"
          :placeholder="
            t(
              'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.FIRST_NAME.PLACEHOLDER'
            )
          "
          :custom-input-class="`h-8 !pt-1 !pb-1 ${!isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''}`"
          class="w-full"
        />
        <!-- Last Name -->
        <Input
          v-model="state.lastName"
          :placeholder="
            t(
              'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.LAST_NAME.PLACEHOLDER'
            )
          "
          :custom-input-class="`h-8 !pt-1 !pb-1 ${!isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''}`"
          class="w-full"
        />
        <!-- Email -->
        <Input
          v-model="state.email"
          :placeholder="
            t(
              'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER'
            )
          "
          :message-type="state.email && v$.email.$invalid ? 'error' : 'info'"
          :custom-input-class="`h-8 !pt-1 !pb-1 ${!isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''}`"
          class="w-full"
        />
        <!-- Phone -->
        <PhoneNumberInput
          v-model="state.phoneNumber"
          :placeholder="
            t(
              'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PHONE_NUMBER.PLACEHOLDER'
            )
          "
          :show-border="isDetailsView"
        />
        <!-- City -->
        <Input
          v-model="state.additionalAttributes.city"
          :placeholder="
            t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.CITY.PLACEHOLDER')
          "
          :custom-input-class="`h-8 !pt-1 !pb-1 ${!isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''}`"
          class="w-full"
        />
        <!-- Country -->
        <ComboBox
          v-model="state.additionalAttributes.countryCode"
          :options="countryOptions"
          :placeholder="
            t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.COUNTRY.PLACEHOLDER')
          "
          class="[&>div>button]:h-8"
          :class="{
            '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
              !isDetailsView,
            '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
          }"
          @update:model-value="handleCountrySelection"
        />
        <!-- Bio -->
        <Input
          v-model="state.additionalAttributes.description"
          :placeholder="
            t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.BIO.PLACEHOLDER')
          "
          :custom-input-class="`h-8 !pt-1 !pb-1 ${!isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''}`"
          class="w-full"
        />
        <!-- Company Name (hidden, we use company search instead) -->
      </div>
    </div>

    <!-- Campo de Empresa -->
    <div ref="companyDropdownRef" class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.COMPANY.TITLE') }}
      </span>
      <div class="relative w-full">
        <div class="relative">
          <Input
            v-model="companySearchQuery"
            :placeholder="t('CONTACTS_LAYOUT.CARD.COMPANY.PLACEHOLDER')"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${
              !isDetailsView
                ? '[&:not(.error,.focus)]:!outline-transparent'
                : ''
            }`"
            class="w-full"
            @input="searchCompanies"
            @focus="showCompanyDropdown = companySearchResults.length > 0"
          />
          <button
            v-if="selectedCompany"
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-n-slate-11 hover:text-n-slate-12"
            @click="clearCompany"
          >
            <Icon icon="i-ri-close-line" class="size-4" />
          </button>
        </div>

        <!-- Dropdown de empresas -->
        <div
          v-if="showCompanyDropdown && companySearchResults.length > 0"
          class="absolute z-50 w-full mt-1 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          <button
            v-for="company in companySearchResults"
            :key="company.id"
            type="button"
            class="w-full px-3 py-2 text-left text-sm hover:bg-n-solid-3 flex items-center gap-2"
            @click="selectCompany(company)"
          >
            <Icon icon="i-ri-building-2-line" class="size-4 text-n-slate-11" />
            <span class="text-n-slate-12">{{ company.name }}</span>
          </button>
        </div>

        <!-- Opção de criar empresa quando não encontrar -->
        <div
          v-if="showCreateCompanyOption"
          class="absolute z-50 w-full mt-1 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg"
        >
          <div class="px-3 py-2 text-xs text-n-slate-11 border-b border-n-weak">
            {{ t('CONTACTS_LAYOUT.CARD.COMPANY.NOT_FOUND') }}
          </div>
          <button
            type="button"
            class="w-full px-3 py-2 text-left text-sm hover:bg-n-solid-3 flex items-center gap-2 text-n-blue-10"
            @click="openCreateCompanyDialog"
          >
            <Icon icon="i-ri-add-line" class="size-4" />
            <span>{{ t('CONTACTS_LAYOUT.CARD.COMPANY.CREATE_NEW') }}</span>
          </button>
        </div>

        <!-- Empresa selecionada -->
        <div
          v-if="selectedCompany"
          class="mt-1 flex items-center gap-2 text-xs text-n-teal-10"
        >
          <Icon icon="i-ri-checkbox-circle-line" class="size-3" />
          <span>{{ selectedCompany.name }}</span>
        </div>

        <!-- Detalhes da empresa -->
        <div
          v-if="selectedCompany && selectedCompany.cnpj"
          class="mt-2 p-2.5 rounded-lg bg-n-alpha-2 dark:bg-n-solid-2 border border-n-weak"
        >
          <div
            class="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-n-slate-12"
          >
            <div v-if="selectedCompany.cnpj" class="flex flex-col">
              <span class="text-n-slate-10">
                {{ t('CONTACT_FORM.FORM.COMPANY_DETAILS.CNPJ') }}
              </span>
              {{ selectedCompany.cnpj }}
            </div>
            <div v-if="selectedCompany.phone" class="flex flex-col">
              <span class="text-n-slate-10">
                {{ t('CONTACT_FORM.FORM.COMPANY_DETAILS.PHONE') }}
              </span>
              {{ selectedCompany.phone }}
            </div>
            <div v-if="selectedCompany.email" class="flex flex-col">
              <span class="text-n-slate-10">
                {{ t('CONTACT_FORM.FORM.COMPANY_DETAILS.EMAIL') }}
              </span>
              {{ selectedCompany.email }}
            </div>
            <div v-if="selectedCompany.description" class="flex flex-col">
              <span class="text-n-slate-10">
                {{ t('CONTACT_FORM.FORM.COMPANY_DETAILS.STATUS') }}
              </span>
              {{ selectedCompany.description }}
            </div>
            <div
              v-if="selectedCompany.address_line1"
              class="flex flex-col col-span-2"
            >
              <span class="text-n-slate-10">
                {{ t('CONTACT_FORM.FORM.COMPANY_DETAILS.ADDRESS') }}
              </span>
              {{ companyAddress }}
            </div>
            <div v-if="companyCityState" class="flex flex-col">
              <span class="text-n-slate-10">
                {{ t('CONTACT_FORM.FORM.COMPANY_DETAILS.CITY') }}
              </span>
              {{ companyCityState }}
            </div>
            <div v-if="selectedCompany.postal_code" class="flex flex-col">
              <span class="text-n-slate-10">
                {{ t('CONTACT_FORM.FORM.COMPANY_DETAILS.POSTAL_CODE') }}
              </span>
              {{ selectedCompany.postal_code }}
            </div>
            <div v-if="companyOpeningDate" class="flex flex-col">
              <span class="text-n-slate-10">
                {{ t('CONTACT_FORM.FORM.COMPANY_DETAILS.OPENING_DATE') }}
              </span>
              {{ companyOpeningDate }}
            </div>
            <div v-if="companyAge" class="flex flex-col">
              <span class="text-n-slate-10">
                {{ t('CONTACT_FORM.FORM.COMPANY_DETAILS.COMPANY_AGE') }}
              </span>
              {{ companyAge }}
            </div>
            <div v-if="companyCapitalSocial" class="flex flex-col">
              <span class="text-n-slate-10">
                {{ t('CONTACT_FORM.FORM.COMPANY_DETAILS.CAPITAL_SOCIAL') }}
              </span>
              {{ companyCapitalSocial }}
            </div>
            <div
              v-if="companyTipoLabel || companyNaturezaJuridica"
              class="flex flex-col"
            >
              <span class="text-n-slate-10">
                {{ t('CONTACT_FORM.FORM.COMPANY_DETAILS.TIPO_EMPRESA') }}
              </span>
              <span v-if="companyTipoLabel" class="font-medium">
                {{ companyTipoLabel }}
              </span>
              <span
                v-if="companyNaturezaJuridica"
                :class="{ 'text-xs text-n-slate-10': companyTipoLabel }"
              >
                {{ companyNaturezaJuridica }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :size="item.placeholder.length"
            @input="emit('update', state)"
          />
        </div>
      </div>
    </div>

    <!-- Dialog para criar empresa -->
    <CreateNewCompanyDialog
      ref="createCompanyDialogRef"
      @create="handleCompanyCreated"
    />
  </div>
</template>
